<template>
  <div class="p-4">
    <!-- Основная информация -->
    <OperationInfoSection
      :data="data"
      :workshop-post-options="workshopPostOptions"
      @change="$emit('change')"
    />

    <!-- Шаги операции -->
    <OperationStepsSection
      :steps="sortedSteps"
      :product-options="productOptions"
      :available-documents="availableDocuments"
      :import-target-step-id="importTargetStepId"
      @change="$emit('change')"
      @add-step="addNewStep"
      @edit-step="editStep"
      @copy-step="(step) => $emit('copy-step', step)"
      @move-step-up="moveStepUp"
      @move-step-down="moveStepDown"
      @add-product="addStepProduct"
      @edit-product="(payload) => editStepProduct(payload.step, payload.index)"
      @add-document="addStepDocument"
      @upload-image="uploadStepImage"
      @remove-product="handleRemoveProduct"
      @remove-step="removeStep"
      @remove-document="removeStepDocument"
      @remove-image="removeStepImage"
      @import-excel="isImportExcelModalOpen = true"
    />

    <!-- Модальные окна -->
    <StepModal
      v-if="isStepModalOpen"
      :step="editingStep"
      :step-types="stepTypes"
      :error="stepError"
      :editing-step-index="editingStepIndex"
      @update:step="updateEditingStep"
      @save="saveStep"
      @close="closeStepModal"
    />

    <ImageModal
      v-if="isImageModalOpen"
      :images="modalImages"
      :error="imageError"
      :uploading="uploadingImage"
      @file-change="handleImageUpload"
      @remove-image="handleRemoveImageFromModal"
      @close="closeImageModal"
    />

    <ProductModal
      v-if="isProductModalOpen"
      :product-options="productOptions"
      :selected-product="selectedProduct"
      :product-count="productCount"
      :is-single="productIsSingle"
      :step-type="currentProductStepType"
      :editing-index="editingProductIndex"
      :loading="loadingProducts"
      :error="productError"
      @update:selected-product="onProductSelected"
      @update:product-count="productCount = $event"
      @update:is-single="productIsSingle = $event"
      @save="saveProduct"
      @close="closeProductModal"
      @reload-products="$emit('reload-products')"
      @search="$emit('search', $event)"
    />

    <DocumentModal
      v-if="isDocumentModalOpen"
      :available-documents="availableDocuments"
      :selected-document="selectedDocument"
      :error="documentError"
      @update:selected-document="selectedDocument = $event"
      @save="confirmAddDocument"
      @close="closeDocumentModal"
    />

    <!-- Удаление шага -->
    <ConfirmationModal
      v-if="isDeleteStepModalOpen"
      title="Удаление шага"
      :message="`Вы уверены, что хотите удалить шаг «${stepToDelete?.name}»?`"
      @confirm="confirmDeleteStep"
      @cancel="closeDeleteStepModal"
    />

    <!-- Удаление продукта -->
    <ConfirmationModal
      v-if="isDeleteProductModalOpen"
      :title="productToDelete.isSoftware ? 'Удаление ПО' : 'Удаление продукта'"
      :message="`Вы уверены, что хотите удалить ${productToDelete.isSoftware ? 'ПО' : 'продукт'} «${productToDelete.name}»?`"
      @confirm="confirmDeleteProduct"
      @cancel="closeDeleteProductModal"
    />

    <!-- Удаление документа -->
    <ConfirmationModal
      v-if="isDeleteDocumentModalOpen"
      title="Удаление документа"
      :message="`Вы уверены, что хотите удалить документ «${documentToDelete?.name}»?`"
      @confirm="confirmDeleteDocument"
      @cancel="closeDeleteDocumentModal"
    />

    <ImportExcelModal
      v-if="isImportExcelModalOpen"
      @imported="handleExcelImported"
      @close="isImportExcelModalOpen = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, toRef } from 'vue';
import axios from 'axios';
import OperationInfoSection from './OperationInfoSection.vue';
import OperationStepsSection from './OperationStepsSection.vue';
import StepModal from './StepModal.vue';
import ImageModal from './ImageModal.vue';
import ProductModal from './ProductModal.vue';
import DocumentModal from './DocumentModal.vue';
import ConfirmationModal from './ConfirmationModal.vue';
import ImportExcelModal from './ImportExcelModal.vue';

export default {
  components: {
    OperationInfoSection,
    OperationStepsSection,
    StepModal,
    ImageModal,
    ProductModal,
    DocumentModal,
    ConfirmationModal,
    ImportExcelModal
  },
  props: {
    data: {
      type: Object,
      required: true,
      validator: (value) => {
        return value && typeof value === 'object' && 'id' in value;
      }
    },
    workshopPosts: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value)
    },
    products: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value)
    },
    documents: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value)
    },
    loadingProducts: {
      type: Boolean,
      default: false
    },
    techMapId: {
      type: [Number, String],
      default: null
    }
  },
  setup(props, { emit }) {
    const isDeleteProductModalOpen = ref(false);
    const productToDelete = ref({
      stepId: null,
      productIndex: null,
      name: '',
      isSoftware: false
    });

    const isDeleteStepModalOpen = ref(false);
    const isDeleteDocumentModalOpen = ref(false);

    const documentToDelete = ref(null);
    const availableDocuments = ref([]);
    const stepTypes = ['production', 'print', 'packaging', 'picking', 'additional', 'software'];

    // Редактирование шагов
    const isStepModalOpen = ref(false);
    const editingStep = ref(null);
    const editingStepIndex = ref(null);
    const stepError = ref('');

    // Изображения
    const isImageModalOpen = ref(false);
    const imageError = ref('');
    const currentStepForImage = ref(null);
    const uploadingImage = ref(false);

    // Продукты
    const isProductModalOpen = ref(false);
    const selectedProduct = ref(null);
    const productCount = ref(1);
    const productIsSingle = ref(false);
    const productError = ref('');
    const currentStepForProduct = ref(null);
    const editingProductIndex = ref(null);

    // Импорт Excel
    const isImportExcelModalOpen = ref(false);

    // Документы
    const isDocumentModalOpen = ref(false);
    const selectedDocument = ref(null);
    const documentError = ref('');
    const currentStepForDocument = ref(null);
    // Обновление списка документов из API
    const refreshDocuments = async () => {
      try {
        const { data } = await axios.get('/api/documents');
        availableDocuments.value = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Ошибка загрузки документов:', error);
        // Не блокируем модалку; оставим список как есть
      }
    };


    // Удаление
    const isDeleteModalOpen = ref(false);
    const stepToDelete = ref(null);
    const stepIndexToDelete = ref(null);
    const productIndexToDelete = ref(null);

    // Вычисляемые свойства
    const sortedSteps = computed(() => {
      if (!props.data.steps) return [];
      return [...props.data.steps].sort((a, b) => (a.sort || 0) - (b.sort || 0));
    });

    // Операция комплектования — показываем кнопку импорта Excel (по типу шага)
    const isPickingOperation = computed(() => (props.data?.steps || []).some(s => s.type === 'picking'));

    // ID шага, к которому привязана кнопка импорта Excel
    // Только первый шаг с «комплект» внутри операции с «комплект»
    const importTargetStepId = computed(() => {
      if (!isPickingOperation.value) return null;
      const steps = props.data.steps || [];
      const target = steps.find(s => s.type === 'picking');
      return target?.id || null;
    });

    // Список продуктов из props (обновляется при загрузке с сервера)
    const productOptions = computed(() => Array.isArray(props.products) ? props.products : []);

    // Локальный список рабочих постов: берём из props, при отсутствии — догружаем
    const workshopPostsLocal = ref([]);
    workshopPostsLocal.value = Array.isArray(props.workshopPosts) ? [...props.workshopPosts] : [];

    watch(() => props.workshopPosts, (newVal) => {
      workshopPostsLocal.value = Array.isArray(newVal) ? [...newVal] : [];
    });

    const workshopPostOptions = computed(() => {
      return (workshopPostsLocal.value || []).map(post => ({
        label: post.name,
        value: post.id,
        id: post.id,
        name: post.name
      }));
    });

    const modalImages = computed(() => {
      const step = currentStepForImage.value;
      if (!step) return [];
      return Array.isArray(step.images) ? [...step.images] : [];
    });

    // Методы для шагов
    const addNewStep = () => {
      editingStep.value = {
        name: '',
        description: '',
        sort: getMaxStepSort() + 5,
        products: [],
        img_url: null,
        images: [],
        type: 'production',
        documents: [],
        print_files: [],
      };
      editingStepIndex.value = null;
      stepError.value = '';
      isStepModalOpen.value = true;
    };

    const editStep = (step, index) => {
      editingStep.value = {
        ...step,
        documents: Array.isArray(step.documents) ? [...step.documents] : [],
        products: Array.isArray(step.products) ? [...step.products] : [],
        images: Array.isArray(step.images) ? [...step.images] : (step.img_url ? [{ url: getStepImageUrl(step.img_url) }] : []),
      };
      editingStepIndex.value = props.data.steps.indexOf(step);
      stepError.value = '';
      isStepModalOpen.value = true;
    };

    const handleRemoveProduct = ({ step, index }) => {
      console.log('Removing product - step:', step, 'index:', index);

      if (!step?.id) {
        console.error('Step has no id');
        return;
      }

      if (!Array.isArray(step.products) || index >= step.products.length) {
        console.error('Invalid products array or index');
        return;
      }

      productToDelete.value = {
        stepId: step.id,
        productIndex: index,
        name: step.products[index]?.name || 'Продукт',
        // В шаге «Используемое ПО» подписи — «ПО», а не «продукт»
        isSoftware: step.type === 'software'
      };
      isDeleteProductModalOpen.value = true;
    };

    const updateEditingStep = (updatedStep) => {
      editingStep.value = updatedStep;
    };

    const saveStep = () => {
      if (!editingStep.value.name?.trim()) {
        stepError.value = 'Введите название шага';
        return;
      }

      const stepData = {
        ...editingStep.value,
        name: editingStep.value.name.trim(),
        documents: editingStep.value.type === 'print'
          ? [...editingStep.value.documents]
          : [],
      };

      if (editingStepIndex.value === null) {
        props.data.steps.push({
          ...stepData,
          id: generateLocalId(),
        });
      } else {
        props.data.steps.splice(editingStepIndex.value, 1, stepData);
      }

      emit('change');
      closeStepModal();
    };

    const removeStep = (index) => {
      const step = sortedSteps.value[index];
      stepToDelete.value = {
        index: props.data.steps.indexOf(step),
        ...step
      };
      isDeleteStepModalOpen.value = true;
    };

    const closeDeleteDocumentModal = () => {
      isDeleteDocumentModalOpen.value = false;
      documentToDelete.value = null;
    };
    const confirmDeleteStep = () => {
      if (stepToDelete.value) {
        props.data.steps.splice(stepToDelete.value.index, 1);
        emit('change');
      }
      closeDeleteStepModal();
    };

    const closeDeleteStepModal = () => {
      isDeleteStepModalOpen.value = false;
      stepToDelete.value = null;
    };

    const removeStepDocument = ({ step, index }) => {
      console.log('Initiating document removal:', {
        stepId: step?.id,
        stepDocuments: step?.documents,
        index
      });

      if (!step?.id) {
        console.error('Step ID is missing! Step object:', step);
        return;
      }

      if (!Array.isArray(step.documents) || index >= step.documents.length) {
        console.error('Invalid documents array or index:', {
          documents: step.documents,
          index
        });
        return;
      }

      const doc = step.documents[index];
      if (!doc?.id) {
        console.error('Document is missing ID:', doc);
        return;
      }

      documentToDelete.value = {
        name: doc.name || `Документ #${index + 1}`,
        stepId: step.id, // Важно: сохраняем ID шага
        docIndex: index,
        documentId: doc.id
      };

      console.log('Document marked for deletion:', documentToDelete.value);
      isDeleteDocumentModalOpen.value = true;
    };

    const confirmDeleteDocument = () => {
      if (!documentToDelete.value?.stepId) {
        console.error('Missing step ID in deletion request:', documentToDelete.value);
        return;
      }

      console.log('Current steps:', props.data.steps.map(s => s.id));
      console.log('Looking for step:', documentToDelete.value.stepId);

      const step = props.data.steps.find(s => s.id === documentToDelete.value.stepId);
      if (!step) {
        console.error('Step not found. Available steps:',
          props.data.steps.map(s => s.id),
          'Looking for:', documentToDelete.value.stepId
        );
        return;
      }

      if (!Array.isArray(step.documents)) {
        console.error('Documents array is invalid in step:', step);
        return;
      }

      const docIndex = step.documents.findIndex(
        d => d.id === documentToDelete.value.documentId
      );

      if (docIndex === -1) {
        console.error('Document not found in step. Available documents:',
          step.documents.map(d => d.id),
          'Looking for:', documentToDelete.value.documentId
        );
        return;
      }

      console.log('Removing document at index:', docIndex);
      step.documents.splice(docIndex, 1);
      emit('change');
      closeDeleteDocumentModal();
    };

    const moveStepUp = (index) => {
      if (index <= 0 || !props.data.steps || props.data.steps.length < 2) return;

      const sorted = sortedSteps.value;
      const currentStep = sorted[index];
      const prevStep = sorted[index - 1];

      if (!currentStep || !prevStep) return;

      // Если sort не заданы или совпадают — нормализуем их перед обменом
      if (currentStep.sort == null || prevStep.sort == null || currentStep.sort === prevStep.sort) {
        prevStep.sort = index - 1;
        currentStep.sort = index;
      }

      [currentStep.sort, prevStep.sort] = [prevStep.sort, currentStep.sort];
      emit('change');
    };

    const moveStepDown = (index) => {
      if (!props.data.steps || props.data.steps.length < 2) return;

      const sorted = sortedSteps.value;
      if (index >= sorted.length - 1) return;

      const currentStep = sorted[index];
      const nextStep = sorted[index + 1];

      if (!currentStep || !nextStep) return;

      if (currentStep.sort == null || nextStep.sort == null || currentStep.sort === nextStep.sort) {
        currentStep.sort = index;
        nextStep.sort = index + 1;
      }

      [currentStep.sort, nextStep.sort] = [nextStep.sort, currentStep.sort];
      emit('change');
    };

    // При выборе продукта — предзаполняем is_single из accounting_type (только при добавлении нового)
    const onProductSelected = (product) => {
      selectedProduct.value = product;
      if (currentStepForProduct.value?.type === 'software') {
        // ПО серийным не учитывается
        productIsSingle.value = false;
        return;
      }
      if (editingProductIndex.value === null && product) {
        productIsSingle.value = product.accounting_type === 'single';
      }
    };

    // Тип шага, в который сейчас добавляется/правится продукт — нужен модалке продукта
    const currentProductStepType = computed(() => currentStepForProduct.value?.type || null);
    const isSoftwareProductStep = () => currentStepForProduct.value?.type === 'software';

    const addStepProduct = async (step) => {
      currentStepForProduct.value = step;
      selectedProduct.value = null;
      productCount.value = 1;
      productIsSingle.value = false;
      productError.value = '';
      editingProductIndex.value = null;
      isProductModalOpen.value = true;
    };

    const editStepProduct = async (step, index) => {
      const product = step.products[index];

      // Ищем в загруженных опциях, если нет — создаём из данных шага
      let availableProduct = props.products.find(p => p.id === product.id);
      if (!availableProduct) {
        const sku = product.sku || '';
        const name = product.name || '';
        availableProduct = {
          id: product.id,
          name,
          sku,
          unit_short_name: product.unit_short_name || 'шт',
          label: sku && name ? `${sku} - ${name}` : (sku || name || 'Без названия'),
          value: product.id,
        };
      }

      currentStepForProduct.value = step;
      editingProductIndex.value = index;
      selectedProduct.value = availableProduct;
      // В шаге «Используемое ПО» количество всегда 1, серийного учёта нет
      productCount.value = isSoftwareProductStep() ? 1 : (product.count || 1);
      productIsSingle.value = isSoftwareProductStep() ? false : !!product.is_single;
      isProductModalOpen.value = true;
    };


    const saveProduct = () => {
      if (!selectedProduct.value) { // Исправлено: проверяем только selectedProduct.value
        productError.value = 'Выберите продукт';
        return;
      }

      if (!productCount.value || productCount.value <= 0) {
        productError.value = 'Укажите корректное количество';
        return;
      }

      // Шаг «Используемое ПО»: количество каждого ПО — 1 шт, серийного учёта нет
      const isSoftware = isSoftwareProductStep();

      const productData = {
        id: selectedProduct.value.id,
        name: selectedProduct.value.name,
        sku: selectedProduct.value.sku,
        count: isSoftware ? 1 : Number(productCount.value),
        is_single: isSoftware ? false : productIsSingle.value,
        unit_short_name: selectedProduct.value.unit_short_name || selectedProduct.value.unit?.short_name || 'шт',
      };

      if (!currentStepForProduct.value.products) {
        currentStepForProduct.value.products = [];
      }

      if (editingProductIndex.value !== null) {
        currentStepForProduct.value.products[editingProductIndex.value] = productData;
      } else {
        currentStepForProduct.value.products.push(productData);
      }

      emit('change');
      closeProductModal();
    };

    const confirmDeleteProduct = () => {
      console.log('Confirming product deletion:', productToDelete.value);

      if (!productToDelete.value.stepId || productToDelete.value.productIndex === null) {
        console.error('Missing stepId or productIndex');
        return;
      }

      // Находим индекс шага, а не сам шаг
      const stepIndex = props.data.steps.findIndex(s => s.id === productToDelete.value.stepId);
      if (stepIndex === -1) {
        console.error('Step not found with id:', productToDelete.value.stepId);
        return;
      }

      // Создаем новый массив продуктов для сохранения реактивности
      const newProducts = [...props.data.steps[stepIndex].products];
      newProducts.splice(productToDelete.value.productIndex, 1);

      // Используем Vue.set или прямое присваивание с spread
      props.data.steps[stepIndex].products = newProducts;

      console.log('Product removed successfully');
      emit('change');
      closeDeleteProductModal();
    };
    const closeDeleteProductModal = () => {
      isDeleteProductModalOpen.value = false;
      productToDelete.value = {
        stepId: null,
        productIndex: null,
        name: ''
      };
    };


    // Методы для документов
    const addStepDocument = async (step) => {
      currentStepForDocument.value = step;
      selectedDocument.value = null;
      documentError.value = '';
      await refreshDocuments();
      isDocumentModalOpen.value = true;
    };

    const confirmAddDocument = () => {
      if (!selectedDocument.value || !currentStepForDocument.value) {
        documentError.value = 'Выберите документ';
        return;
      }

      if (!Array.isArray(currentStepForDocument.value.documents)) {
        currentStepForDocument.value.documents = [];
      }

      currentStepForDocument.value.documents.push({
        id: selectedDocument.value.id,
        name: selectedDocument.value.name,
      });

      emit('change');
      closeDocumentModal();
    };

    // Методы для изображений
    const uploadStepImage = (step) => {
      currentStepForImage.value = step;
      if (!Array.isArray(step.images)) {
        step.images = step.img_url ? [{ url: getStepImageUrl(step.img_url) }] : [];
      }
      imageError.value = '';
      isImageModalOpen.value = true;
    };

    const handleImageUpload = async (files) => {
      const step = currentStepForImage.value;
      if (!step || !Array.isArray(files) || files.length === 0) return;

      uploadingImage.value = true;
      imageError.value = '';

      for (const file of files) {
        if (!file.type?.startsWith('image/')) continue;
        try {
          const formData = new FormData();
          formData.append('image', file);
          formData.append('operation_id', props.data.id);
          const stepId = step.id;
          const isLocalStep = typeof stepId === 'string' && stepId.startsWith('local-');
          formData.append(isLocalStep ? 'temp_step_id' : 'step_id', stepId);

          const { data } = await axios.post('/api/productionOperation/uploadStepImage', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (data?.success) {
            const item = { url: data.image_url, file_id: data.file_id };
            if (!Array.isArray(step.images)) step.images = [];
            step.images.push(item);
            emit('change');
          } else {
            imageError.value = data?.message || 'Ошибка загрузки изображения';
          }
        } catch (error) {
          imageError.value = error.response?.data?.message || 'Ошибка загрузки изображения';
          console.error('Ошибка загрузки изображения:', error);
        }
      }

      uploadingImage.value = false;
    };

    const handleRemoveImageFromModal = (index) => {
      const step = currentStepForImage.value;
      if (!step || !Array.isArray(step.images) || index < 0 || index >= step.images.length) return;
      step.images.splice(index, 1);
      emit('change');
    };

    const removeStepImage = ({ step, imageIndex }) => {
      const stepIndex = props.data.steps.findIndex(s => s.id === step.id);
      if (stepIndex === -1) return;
      const s = props.data.steps[stepIndex];
      if (Array.isArray(s.images) && imageIndex >= 0 && imageIndex < s.images.length) {
        s.images.splice(imageIndex, 1);
        emit('change');
      }
    };

    // Вспомогательные методы
    const getMaxStepSort = () => {
      if (!props.data.steps?.length) return 0;
      return Math.max(...props.data.steps.map(step => step.sort || 0));
    };

    const generateLocalId = () => `local-step-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const getStepImageUrl = (url) => {
      if (!url) return '';
      if (url.startsWith('/') || url.startsWith('http')) return url;
      return `/operation-steps-images/${url}`;
    };

    // Методы закрытия модальных окон
    const closeStepModal = () => {
      isStepModalOpen.value = false;
      stepError.value = '';
    };

    const closeImageModal = () => {
      isImageModalOpen.value = false;
      imageError.value = '';
      currentStepForImage.value = null;
    };

    const closeProductModal = () => {
      isProductModalOpen.value = false;
      selectedProduct.value = null;
      productCount.value = 1;
      productError.value = '';
      currentStepForProduct.value = null;
      editingProductIndex.value = null;
    };

    const closeDocumentModal = () => {
      isDocumentModalOpen.value = false;
      selectedDocument.value = null;
      documentError.value = '';
      currentStepForDocument.value = null;
    };

    const closeDeleteModal = () => {
      isDeleteModalOpen.value = false;
      stepToDelete.value = null;
      documentToDelete.value = null;  // Сбрасываем и документ
    };

    // Загрузка данных
    const ensureWorkshopPostsLoaded = async () => {
      if (Array.isArray(workshopPostsLocal.value) && workshopPostsLocal.value.length > 0) return;
      try {
        const { data } = await axios.post('/api/workshopPost/getWorkshopPosts');
        const list = data?.data || [];
        if (Array.isArray(list) && list.length > 0) {
          workshopPostsLocal.value = list;
        }
      } catch (error) {
        console.error('Ошибка загрузки рабочих постов:', error);
      }
    };

    onMounted(async () => {
      if (!props.data) {
        console.error('Operation data is missing');
      }
      if (props.workshopPosts.length === 0) {
        console.warn('Workshop posts list is empty');
      }
      if (props.products.length === 0) {
        console.warn('Products list is empty');
      }
      if (props.documents.length === 0) {
        console.warn('Documents list is empty');
      }
      // Используем документы, переданные из родителя
      availableDocuments.value = props.documents;

      // Инициализация steps, если их нет
      if (!props.data.steps) {
        emit('update:data', { ...props.data, steps: [] });
      } else {
        // Инициализация documents и images в шагах
        props.data.steps.forEach(step => {
          if (!Array.isArray(step.documents)) {
            step.documents = [];
          }
          if (!Array.isArray(step.images) && step.img_url) {
            step.images = [{ url: getStepImageUrl(step.img_url) }];
          } else if (!Array.isArray(step.images)) {
            step.images = [];
          }
        });
      }

      // Гарантируем наличие рабочих постов для селекта
      await ensureWorkshopPostsLoaded();
    });

    // Следим за изменениями документов из родителя
    watch(() => props.documents, (newDocs) => {
      availableDocuments.value = newDocs;
    });

    // Возвращаем все, что используется в шаблоне
    return {
      // Состояние
      editingStepIndex,
      isDeleteDocumentModalOpen,
      isDeleteStepModalOpen,
      documentToDelete,
      availableDocuments,
      stepTypes,
      isStepModalOpen,
      editingStep,
      stepError,
      isImageModalOpen,
      modalImages,
      imageError,
      uploadingImage,
      isProductModalOpen,
      selectedProduct,
      productCount,
      productError,
      currentProductStepType,
      loadingProducts: toRef(props, 'loadingProducts'),
      isDocumentModalOpen,
      selectedDocument,
      documentError,
      isDeleteModalOpen,
      stepToDelete,
      isDeleteProductModalOpen,
      productToDelete,
      // Вычисляемые свойства
      sortedSteps,
      productOptions,
      workshopPostOptions,

      // Методы
      removeStepImage,
      handleRemoveProduct,
      closeDeleteDocumentModal,
      closeDeleteStepModal,
      removeStepDocument,
      confirmDeleteDocument,
      addNewStep,
      editStep,
      updateEditingStep,
      saveStep,
      removeStep,
      confirmDeleteStep,
      moveStepUp,
      moveStepDown,
      addStepProduct,
      editStepProduct,
      saveProduct,
      confirmDeleteProduct,
      addStepDocument,
      confirmAddDocument,
      uploadStepImage,
      handleImageUpload,
      handleRemoveImageFromModal,
      closeStepModal,
      closeImageModal,
      closeProductModal,
      closeDocumentModal,
      closeDeleteModal,
      closeDeleteProductModal,
      getStepImageUrl,
      productIsSingle,
      // Без него модалка продукта не знает, что идёт правка, и пишет «Добавить» вместо «Редактировать»
      editingProductIndex,
      onProductSelected,
      importTargetStepId,
      isImportExcelModalOpen,
      isPickingOperation,
      handleExcelImported(products) {
        // Вставляем продукты в первый шаг с «комплект»
        const steps = props.data.steps || [];
        const targetStep = steps.find(s => s.type === 'picking');
        if (targetStep && Array.isArray(products)) {
          if (!targetStep.products) targetStep.products = [];
          products.forEach(p => {
            targetStep.products.push(p);
          });
          emit('change');
        }
      }
    };
  }
};
</script>
